import React, { useCallback, useEffect, useState } from 'react';
import {
    AccessUserRow,
    listAccessUsers,
    promoteUser,
    revokeAccess,
} from '../../api/access';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api/request';
import { formatTime } from '../../utils/time';
import Pagination from '../../components/Pagination';
import SearchSelect from '../../components/SearchSelect';
import UserPermissionModal from './UserPermissionModal';
import { confirmDialog, alertDialog } from '../../utils/dialog';

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<string, string> = {
    pending: '待审批',
    approved: '已通过',
    rejected: '已驳回',
};

const STATUS_OPTS = Object.entries(STATUS_LABEL).map(([value, label]) => ({
    value,
    label,
}));

const STATUS_CLASS: Record<string, string> = {
    pending: 'text-amber-600 bg-amber-50 border-amber-200',
    approved: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    rejected: 'text-rose-600 bg-rose-50 border-rose-200',
};

/**
 * 普通用户访问名单 (2026-08-25): 申请-审批制.
 * passport 登录后提交申请, 在此审批; 管理员 (管理员 tab) 不经本名单默认可访问.
 */
function AccessUserList() {
    const [status, setStatus] = useState('');
    const [permEmail, setPermEmail] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [keyword, setKeyword] = useState('');
    const [page, setPage] = useState(1);
    const [list, setList] = useState<AccessUserRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listAccessUsers({
                status,
                keyword,
                page,
                pageSize: PAGE_SIZE,
            });
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [status, keyword, page]);

    useEffect(() => {
        load();
    }, [load]);

    // 输入即搜 (2026-09-12): 停止输入 300ms 后自动按关键词过滤, 不必点「查询」; 清空即恢复全量.
    useEffect(() => {
        const t = window.setTimeout(() => {
            const kw = input.trim();
            if (kw !== keyword) {
                setKeyword(kw);
                setPage(1);
            }
        }, 300);
        return () => window.clearTimeout(t);
    }, [input, keyword]);

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setKeyword(input.trim());
        setPage(1);
    };

    const onPromote = async (row: AccessUserRow) => {
        if (
            !(await confirmDialog(
                `确认将 ${row.displayName || row.email || row.uid} 设为管理员?`,
            ))
        )
            return;
        try {
            await promoteUser(row.uid);
            await load();
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog(`设为管理员失败: ${msg}`);
        }
    };

    const onRevoke = async (row: AccessUserRow) => {
        if (
            !(await confirmDialog(
                `确认将 ${row.displayName || row.email || row.uid} 移出平台?\n将删除该用户及其全部角色, 对方需重新申请准入。`,
            ))
        )
            return;
        try {
            await revokeAccess(row.uid);
            await load();
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog(`移除失败: ${msg}`);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">普通用户</div>
                    <div className="text-xs text-slate-500">
                        申请-审批制 · 待审批的申请在
                        <Link
                            to="/admin/approvals?tab=access"
                            className="text-brand-600 hover:underline mx-1"
                        >
                            审批中心
                        </Link>
                        处理 · 共 {total} 条
                    </div>
                </div>
                <form onSubmit={onSearch} className="flex gap-2 items-center">
                    <SearchSelect
                        className="w-32"
                        value={status}
                        onChange={(v) => {
                            setStatus(v);
                            setPage(1);
                        }}
                        placeholder="全部状态"
                        options={STATUS_OPTS}
                        clearable
                    />
                    <input
                        className="inp w-56"
                        placeholder="搜索 邮箱 / 用户名"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onBlur={() => {
                            if (input.trim() === '' && keyword !== '') {
                                setKeyword('');
                                setPage(1);
                            }
                        }}
                    />
                    <button type="submit" className="btn-ghost">
                        查询
                    </button>
                </form>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th">用户</th>
                            <th className="th">邮箱</th>
                            <th className="th">状态</th>
                            <th className="th">申请理由</th>
                            <th className="th">申请时间</th>
                            <th className="th">审批人</th>
                            <th className="th">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={7}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={7}
                                >
                                    暂无申请记录
                                </td>
                            </tr>
                        )}
                        {list.map((u) => (
                            <tr key={u.id} className="hover:bg-slate-50">
                                <td className="td">{u.displayName || u.uid}</td>
                                <td className="td font-mono text-xs">
                                    {u.email || '—'}
                                </td>
                                <td className="td">
                                    <span
                                        className={`inline-block px-2 py-0.5 rounded border text-xs ${
                                            STATUS_CLASS[u.status] || ''
                                        }`}
                                    >
                                        {STATUS_LABEL[u.status] || u.status}
                                    </span>
                                </td>
                                <td
                                    className="td text-xs text-slate-500 max-w-[240px] truncate"
                                    title={u.reason}
                                >
                                    {u.reason || '—'}
                                </td>
                                <td className="td text-xs text-slate-500">
                                    {formatTime(u.createdAt)}
                                </td>
                                <td className="td text-xs text-slate-500">
                                    {u.reviewedBy
                                        ? u.reviewedByName || u.reviewedBy
                                        : '—'}
                                </td>
                                <td className="td text-xs space-x-3 whitespace-nowrap">
                                    {u.status === 'pending' && (
                                        <Link
                                            to="/admin/approvals?tab=access"
                                            className="text-brand-600 hover:underline"
                                        >
                                            去审批
                                        </Link>
                                    )}
                                    {u.status === 'approved' && u.email && (
                                        <button
                                            type="button"
                                            className="text-brand-600 hover:underline"
                                            onClick={() =>
                                                setPermEmail(u.email)
                                            }
                                        >
                                            权限分配
                                        </button>
                                    )}
                                    {u.status === 'approved' && (
                                        <button
                                            type="button"
                                            className="text-brand-600 hover:underline"
                                            onClick={() => onPromote(u)}
                                        >
                                            设为管理员
                                        </button>
                                    )}
                                    {u.status !== 'pending' && (
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            onClick={() => onRevoke(u)}
                                        >
                                            {u.status === 'approved'
                                                ? '移出平台'
                                                : '删除记录'}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 条</span>
                <div className="flex items-center gap-2">
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={setPage}
                    />
                </div>
            </div>
            <UserPermissionModal
                email={permEmail}
                onClose={() => setPermEmail(null)}
            />
        </div>
    );
}

export default AccessUserList;
